import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for smart positioning of floating elements
 * Calculates the best position (above or below) based on available viewport space
 */
export const useSmartPosition = (referenceElement, floatingElement, isVisible) => {
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    placement: 'bottom' // 'top' or 'bottom'
  });

  const calculatePosition = useCallback(() => {
    if (!referenceElement || !floatingElement || !isVisible) {
      return;
    }

    const referenceRect = referenceElement.getBoundingClientRect();
    const floatingRect = floatingElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Check if we have valid dimensions
    if (referenceRect.width === 0 || referenceRect.height === 0) {
      return; // Reference element not yet rendered
    }

    // Calculate available space above and below the reference element
    const spaceAbove = referenceRect.top;
    const spaceBelow = viewportHeight - referenceRect.bottom;
    
    // Menu dimensions - use computed style if getBoundingClientRect returns 0
    let menuWidth = floatingRect.width;
    let menuHeight = floatingRect.height;
    
    if (menuWidth === 0 || menuHeight === 0) {
      // Fallback to CSS computed values or default
      const computedStyle = window.getComputedStyle(floatingElement);
      menuWidth = parseInt(computedStyle.width) || 180;
      menuHeight = parseInt(computedStyle.height) || 200;
    }

    // Determine placement (prefer below, but use above if not enough space)
    let placement = 'bottom';
    let top = referenceRect.bottom + 8; // 8px offset below

    if (spaceBelow < menuHeight && spaceAbove > menuHeight) {
      placement = 'top';
      top = referenceRect.top - menuHeight - 8; // 8px offset above
    }

    // Calculate horizontal position (centered on reference element)
    let left = referenceRect.left + (referenceRect.width / 2) - (menuWidth / 2);
    
    // Ensure the menu doesn't go outside viewport horizontally
    const padding = 16; // 16px padding from viewport edge
    if (left < padding) {
      left = padding;
    } else if (left + menuWidth > viewportWidth - padding) {
      left = viewportWidth - menuWidth - padding;
    }

    setPosition({
      top: Math.max(0, top),
      left: Math.max(0, left),
      placement
    });
  }, [referenceElement, floatingElement, isVisible]);

  useEffect(() => {
    if (isVisible) {
      // Use requestAnimationFrame to ensure DOM has been updated
      const timeoutId = requestAnimationFrame(() => {
        calculatePosition();
      });
      
      // Recalculate on window resize or scroll
      const handleUpdate = () => calculatePosition();
      window.addEventListener('resize', handleUpdate);
      window.addEventListener('scroll', handleUpdate, true);
      
      return () => {
        cancelAnimationFrame(timeoutId);
        window.removeEventListener('resize', handleUpdate);
        window.removeEventListener('scroll', handleUpdate, true);
      };
    }
  }, [calculatePosition, isVisible]);

  return position;
};